import { UINodeConfig, LayoutRect, UIStyle, DEFAULT_STYLE, resolvePadding, buildFont } from './types';

type RoundRectContext = CanvasRenderingContext2D & {
  roundRect: (x: number, y: number, width: number, height: number, radii: number) => void;
};

function resolveStyle(style?: UIStyle): UIStyle {
  return { ...DEFAULT_STYLE, ...(style ?? {}) };
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
  ctx.beginPath();
  const roundRectContext = ctx as RoundRectContext;
  if (typeof roundRectContext.roundRect === 'function') {
    roundRectContext.roundRect(x, y, width, height, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function paintTree(
  ctx: CanvasRenderingContext2D,
  node: UINodeConfig,
  rects: Map<UINodeConfig, LayoutRect>,
  scale: number,
): void {
  const paintNode = (current: UINodeConfig): void => {
    const rect = rects.get(current);
    if (!rect) return;

    const style = resolveStyle(current.style);
    const [, padRight, , padLeft] = resolvePadding(style.padding);
    const opacity = style.opacity ?? DEFAULT_STYLE.opacity;

    let didSave = false;
    if (opacity < 1) {
      ctx.save();
      ctx.globalAlpha *= opacity;
      didSave = true;
    }

    if (style.backgroundColor) {
      ctx.fillStyle = style.backgroundColor;
      drawRoundedRect(ctx, rect.x, rect.y, rect.width, rect.height, style.borderRadius ?? DEFAULT_STYLE.borderRadius);
      ctx.fill();
    }

    const borderWidth = style.borderWidth ?? DEFAULT_STYLE.borderWidth;
    if (style.borderColor && borderWidth > 0) {
      ctx.lineWidth = borderWidth;
      ctx.strokeStyle = style.borderColor;
      drawRoundedRect(ctx, rect.x, rect.y, rect.width, rect.height, style.borderRadius ?? DEFAULT_STYLE.borderRadius);
      ctx.stroke();
    }

    if (current.triangle) {
      const tri = current.triangle;
      const dir = tri.direction ?? 'right';
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const hw = tri.width / 2;
      const hh = tri.height / 2;

      ctx.beginPath();
      if (dir === 'right') {
        ctx.moveTo(cx - hw, cy - hh);
        ctx.lineTo(cx + hw, cy);
        ctx.lineTo(cx - hw, cy + hh);
      } else if (dir === 'left') {
        ctx.moveTo(cx + hw, cy - hh);
        ctx.lineTo(cx - hw, cy);
        ctx.lineTo(cx + hw, cy + hh);
      } else if (dir === 'up') {
        ctx.moveTo(cx - hw, cy + hh);
        ctx.lineTo(cx, cy - hh);
        ctx.lineTo(cx + hw, cy + hh);
      } else {
        ctx.moveTo(cx - hw, cy - hh);
        ctx.lineTo(cx, cy + hh);
        ctx.lineTo(cx + hw, cy - hh);
      }
      ctx.closePath();
      if (tri.fill) {
        ctx.fillStyle = tri.fill;
        ctx.fill();
      }
    }

    if (current.circle) {
      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;
      const radius = current.circle.radius;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      if (current.circle.fill) {
        ctx.fillStyle = current.circle.fill;
        ctx.fill();
      }
      if (current.circle.stroke && (current.circle.strokeWidth ?? 0) > 0) {
        ctx.strokeStyle = current.circle.stroke;
        ctx.lineWidth = current.circle.strokeWidth ?? 1;
        ctx.stroke();
      }

      if (current.circle.image) {
        // Clip to the circle, then draw the image with "cover" fit so it FILLS
        // the circle (corners extend past the boundary and get clipped round).
        // Drawing it smaller than the circle leaves square corners inside the
        // clip — which is why opaque photos showed as a square.
        const img = current.circle.image;
        const d = radius * 2;
        const iw = img.naturalWidth || d;
        const ih = img.naturalHeight || d;
        const scale = Math.max(d / iw, d / ih);
        const w = iw * scale;
        const h = ih * scale;
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, centerX - w / 2, centerY - h / 2, w, h);
        ctx.restore();
        // Re-stroke the ring on top so it stays crisp over the photo edge.
        if (current.circle.stroke && (current.circle.strokeWidth ?? 0) > 0) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.strokeStyle = current.circle.stroke;
          ctx.lineWidth = current.circle.strokeWidth ?? 1;
          ctx.stroke();
        }
      } else if (current.circle.text) {
        const textSize = current.circle.textSize ?? 14;
        ctx.font = `normal ${textSize}px ${style.fontFamily ?? DEFAULT_STYLE.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = style.color ?? DEFAULT_STYLE.color;
        ctx.fillText(current.circle.text, centerX, centerY);
      }
    }

    if (current.image) {
      ctx.drawImage(current.image, rect.x, rect.y, rect.width, rect.height);
    }

    if (current.text) {
      ctx.font = buildFont(style);
      ctx.fillStyle = style.color ?? DEFAULT_STYLE.color;
      ctx.textBaseline = 'middle';
      ctx.textAlign = style.textAlign ?? DEFAULT_STYLE.textAlign;

      // Truncate with ellipsis if text overflows available width
      const availableWidth = rect.width - padLeft - padRight;
      let displayText = current.text;
      if (style.overflow === 'ellipsis' && ctx.measureText(displayText).width > availableWidth) {
        const ellipsis = '...';
        const ellipsisWidth = ctx.measureText(ellipsis).width;
        let truncated = displayText;
        while (truncated.length > 0 && ctx.measureText(truncated).width + ellipsisWidth > availableWidth) {
          truncated = truncated.slice(0, -1);
        }
        displayText = truncated + ellipsis;
      }

      let textX = rect.x + padLeft;
      if (ctx.textAlign === 'center') {
        textX = rect.x + rect.width / 2;
      } else if (ctx.textAlign === 'right') {
        textX = rect.x + rect.width - padRight;
      }

      const textY = rect.y + rect.height / 2;
      ctx.fillText(displayText, textX, textY);
    }

    if (current.children) {
      for (const child of current.children) {
        paintNode(child);
      }
    }

    if (didSave) {
      ctx.restore();
    }
  };

  void scale;
  paintNode(node);
}

export { drawRoundedRect };
